import { Locale } from "@/i18n-config";
import { defineLocaleRecord } from "@/i18n-config";

export type CoursesMarketplaceCopy = {
  title: string;
  subtitle: string;
  seoTitle: string;
  seoDescription: string;
  introPrimary: string;
  introSecondary: string;
  quickLinksLabel: string;
  helpLinkLabel: string;
  updatesLinkLabel: string;
  helper: string;
  filtersTitle: string;
  filters: {
    category: string;
    time: string;
    difficulty: string;
    access: string;
  };
  categories: {
    coding: string;
    practice: string;
    mindset: string;
  };
  timeOptions: {
    lt30: string;
    min30to60: string;
    hr1to2: string;
  };
  difficultyOptions: {
    beginner: string;
    standard: string;
    advanced: string;
  };
  accessOptions: {
    free: string;
    paid: string;
  };
  sortLabel: string;
  sort: {
    recommended: string;
    shortest: string;
    beginnerFriendly: string;
    newest: string;
  };
  openFilters: string;
  closeFilters: string;
  resultsLabel: string;
  clearFilters: string;
  empty: string;
  viewDetails: string;
  noPreview: string;
  problemFallback: string;
  durationUnit: string;
};

export const coursesMarketplaceCopyByLocale = defineLocaleRecord<CoursesMarketplaceCopy>({
  en: {
    title: "Courses",
    subtitle: "Compare courses by time, difficulty, and focus.",
    seoTitle: "AI Courses for Non-Technical Builders",
    seoDescription:
      "Browse step-by-step AI and no-code courses to turn ideas into working products. Filter by time, difficulty, and focus.",
    introPrimary: "Build a working product with step-by-step AI courses designed for non-technical builders.",
    introSecondary: "Pick a practical lesson path by time, difficulty, and goal, then move one blocker forward today.",
    quickLinksLabel: "Explore next",
    helpLinkLabel: "Help / FAQ",
    updatesLinkLabel: "Product updates",
    helper: "More courses coming.",
    filtersTitle: "Filters",
    filters: {
      category: "Category",
      time: "Time to complete",
      difficulty: "Difficulty",
      access: "Access",
    },
    categories: {
      coding: "Coding",
      practice: "Practice",
      mindset: "Mindset",
    },
    timeOptions: {
      lt30: "< 30 min",
      min30to60: "30–60 min",
      hr1to2: "1–2 hours",
    },
    difficultyOptions: {
      beginner: "Beginner",
      standard: "Standard",
      advanced: "Advanced",
    },
    accessOptions: {
      free: "Free",
      paid: "Paid",
    },
    sortLabel: "Sort",
    sort: {
      recommended: "Recommended",
      shortest: "Shortest first",
      beginnerFriendly: "Beginner friendly",
      newest: "New",
    },
    openFilters: "Filters",
    closeFilters: "Close filters",
    resultsLabel: "{count} courses",
    clearFilters: "Clear filters",
    empty: "No courses match these filters.",
    viewDetails: "View course",
    noPreview: "No preview",
    problemFallback: "Moves one blocker forward.",
    durationUnit: "min",
  },
  fr: {
    title: "Parcours",
    subtitle: "Comparez les parcours par temps, niveau et focus.",
    seoTitle: "Parcours IA pour creer un produit sans bagage technique",
    seoDescription:
      "Explorez des parcours IA pas a pas pour transformer une idee en produit fonctionnel. Filtrez par duree, niveau et objectif.",
    introPrimary: "Choisissez un parcours IA pratique pour avancer sans confusion technique.",
    introSecondary: "Filtrez par duree, niveau et objectif, puis passez a une action concrete.",
    quickLinksLabel: "Explorer ensuite",
    helpLinkLabel: "Aide / FAQ",
    updatesLinkLabel: "Mises a jour produit",
    helper: "D'autres parcours arrivent.",
    filtersTitle: "Filtres",
    filters: {
      category: "Categorie",
      time: "Temps pour terminer",
      difficulty: "Niveau",
      access: "Acces",
    },
    categories: {
      coding: "Code",
      practice: "Pratique",
      mindset: "Mindset",
    },
    timeOptions: {
      lt30: "< 30 min",
      min30to60: "30–60 min",
      hr1to2: "1–2 heures",
    },
    difficultyOptions: {
      beginner: "Debutant",
      standard: "Standard",
      advanced: "Avance",
    },
    accessOptions: {
      free: "Gratuit",
      paid: "Payant",
    },
    sortLabel: "Tri",
    sort: {
      recommended: "Recommande",
      shortest: "Plus court d'abord",
      beginnerFriendly: "Debutant",
      newest: "Nouveau",
    },
    openFilters: "Filtres",
    closeFilters: "Fermer les filtres",
    resultsLabel: "{count} parcours",
    clearFilters: "Effacer les filtres",
    empty: "Aucun parcours ne correspond a ces filtres.",
    viewDetails: "Voir details",
    noPreview: "Aucun apercu",
    problemFallback: "Fait avancer un blocage.",
    durationUnit: "min",
  },
  ru: {
    title: "Курсы",
    subtitle: "Сравните курсы по времени, сложности и фокусу.",
    seoTitle: "AI-курсы для нетехнических создателей",
    seoDescription:
      "Изучайте пошаговые AI-курсы и no-code маршруты, чтобы превратить идею в рабочий продукт. Фильтруйте по времени, сложности и цели.",
    introPrimary: "Выберите практический AI-курс и двигайтесь к рабочему продукту без технической перегрузки.",
    introSecondary: "Фильтруйте по времени, сложности и фокусу, чтобы сделать следующий понятный шаг.",
    quickLinksLabel: "Полезные ссылки",
    helpLinkLabel: "Помощь / FAQ",
    updatesLinkLabel: "Обновления продукта",
    helper: "Скоро будет больше курсов.",
    filtersTitle: "Фильтры",
    filters: {
      category: "Категория",
      time: "Время на прохождение",
      difficulty: "Сложность",
      access: "Доступ",
    },
    categories: {
      coding: "Разработка",
      practice: "Практика",
      mindset: "Мышление",
    },
    timeOptions: {
      lt30: "< 30 мин",
      min30to60: "30–60 мин",
      hr1to2: "1–2 часа",
    },
    difficultyOptions: {
      beginner: "Начальный",
      standard: "Стандартный",
      advanced: "Продвинутый",
    },
    accessOptions: {
      free: "Бесплатно",
      paid: "Платно",
    },
    sortLabel: "Сортировка",
    sort: {
      recommended: "Рекомендуемые",
      shortest: "Сначала короткие",
      beginnerFriendly: "Для начинающих",
      newest: "Новые",
    },
    openFilters: "Фильтры",
    closeFilters: "Закрыть фильтры",
    resultsLabel: "{count} курсов",
    clearFilters: "Сбросить фильтры",
    empty: "Нет курсов с такими фильтрами.",
    viewDetails: "Открыть",
    noPreview: "Нет превью",
    problemFallback: "Продвигает один блокер вперед.",
    durationUnit: "мин",
  },
  uk: {
    title: "Курси",
    subtitle: "Порівняйте курси за часом, складністю та фокусом.",
    seoTitle: "AI-курси для нетехнічних творців",
    seoDescription:
      "Проходьте покрокові AI-курси та no-code маршрути, щоб перетворити ідею на робочий продукт. Фільтруйте за часом, складністю та ціллю.",
    introPrimary: "Оберіть практичний AI-курс і рухайтесь до робочого продукту без технічного перевантаження.",
    introSecondary: "Фільтруйте за часом, складністю та фокусом, щоб зробити наступний чіткий крок.",
    quickLinksLabel: "Корисні посилання",
    helpLinkLabel: "Допомога / FAQ",
    updatesLinkLabel: "Оновлення продукту",
    helper: "Скоро буде більше курсів.",
    filtersTitle: "Фільтри",
    filters: {
      category: "Категорія",
      time: "Час на проходження",
      difficulty: "Складність",
      access: "Доступ",
    },
    categories: {
      coding: "Розробка",
      practice: "Практика",
      mindset: "Мислення",
    },
    timeOptions: {
      lt30: "< 30 хв",
      min30to60: "30–60 хв",
      hr1to2: "1–2 години",
    },
    difficultyOptions: {
      beginner: "Початковий",
      standard: "Стандартний",
      advanced: "Просунутий",
    },
    accessOptions: {
      free: "Безкоштовно",
      paid: "Платно",
    },
    sortLabel: "Сортування",
    sort: {
      recommended: "Рекомендовані",
      shortest: "Спочатку короткі",
      beginnerFriendly: "Для початківців",
      newest: "Нові",
    },
    openFilters: "Фільтри",
    closeFilters: "Закрити фільтри",
    resultsLabel: "{count} курсів",
    clearFilters: "Очистити фільтри",
    empty: "Немає курсів з такими фільтрами.",
    viewDetails: "Відкрити",
    noPreview: "Немає превʼю",
    problemFallback: "Рухає один блокер вперед.",
    durationUnit: "хв",
  },
  de: {
    title: "Kurse",
    subtitle: "Vergleiche Kurse nach Zeit, Niveau und Fokus.",
    seoTitle: "KI-Kurse fuer Nicht-Techniker mit klaren Schritten",
    seoDescription:
      "Finde Schritt-fuer-Schritt KI- und No-Code-Kurse, um Ideen in funktionierende Produkte umzusetzen. Filtere nach Dauer, Niveau und Fokus.",
    introPrimary: "Waehle einen praxisnahen KI-Kurs und baue ein echtes Ergebnis ohne technischen Overload.",
    introSecondary: "Filtere nach Dauer, Niveau und Fokus und gehe den naechsten klaren Umsetzungsschritt.",
    quickLinksLabel: "Naechste Bereiche",
    helpLinkLabel: "Hilfe / FAQ",
    updatesLinkLabel: "Produkt-Updates",
    helper: "Weitere Kurse kommen bald.",
    filtersTitle: "Filter",
    filters: {
      category: "Kategorie",
      time: "Zeitaufwand",
      difficulty: "Niveau",
      access: "Zugang",
    },
    categories: {
      coding: "Coding",
      practice: "Praxis",
      mindset: "Mindset",
    },
    timeOptions: {
      lt30: "< 30 Min",
      min30to60: "30–60 Min",
      hr1to2: "1–2 Stunden",
    },
    difficultyOptions: {
      beginner: "Anfaenger",
      standard: "Standard",
      advanced: "Fortgeschritten",
    },
    accessOptions: {
      free: "Kostenlos",
      paid: "Bezahlt",
    },
    sortLabel: "Sortierung",
    sort: {
      recommended: "Empfohlen",
      shortest: "Kuerzeste zuerst",
      beginnerFriendly: "Einsteigerfreundlich",
      newest: "Neu",
    },
    openFilters: "Filter",
    closeFilters: "Filter schliessen",
    resultsLabel: "{count} Kurse",
    clearFilters: "Filter zuruecksetzen",
    empty: "Keine Kurse passen zu diesen Filtern.",
    viewDetails: "Details ansehen",
    noPreview: "Keine Vorschau",
    problemFallback: "Bringt einen Blocker weiter.",
    durationUnit: "Min",
  },
  es: {
    title: "Cursos",
    subtitle: "Compara cursos por tiempo, dificultad y enfoque.",
    seoTitle: "Cursos de IA para creadores sin perfil tecnico",
    seoDescription:
      "Explora cursos de IA y no-code paso a paso para convertir ideas en productos funcionales. Filtra por tiempo, dificultad y enfoque.",
    introPrimary: "Elige un curso practico de IA y avanza hacia un producto real sin sobrecarga tecnica.",
    introSecondary: "Filtra por tiempo, dificultad y objetivo para completar el siguiente paso con claridad.",
    quickLinksLabel: "Explorar despues",
    helpLinkLabel: "Ayuda / FAQ",
    updatesLinkLabel: "Actualizaciones de producto",
    helper: "Pronto habra mas cursos.",
    filtersTitle: "Filtros",
    filters: {
      category: "Categoria",
      time: "Tiempo para completar",
      difficulty: "Dificultad",
      access: "Acceso",
    },
    categories: {
      coding: "Coding",
      practice: "Practica",
      mindset: "Mentalidad",
    },
    timeOptions: {
      lt30: "< 30 min",
      min30to60: "30–60 min",
      hr1to2: "1–2 horas",
    },
    difficultyOptions: {
      beginner: "Principiante",
      standard: "Estandar",
      advanced: "Avanzado",
    },
    accessOptions: {
      free: "Gratis",
      paid: "De pago",
    },
    sortLabel: "Ordenar",
    sort: {
      recommended: "Recomendado",
      shortest: "Mas corto primero",
      beginnerFriendly: "Para principiantes",
      newest: "Nuevo",
    },
    openFilters: "Filtros",
    closeFilters: "Cerrar filtros",
    resultsLabel: "{count} cursos",
    clearFilters: "Limpiar filtros",
    empty: "Ningun curso coincide con estos filtros.",
    viewDetails: "Ver detalles",
    noPreview: "Sin vista previa",
    problemFallback: "Avanza un bloqueo.",
    durationUnit: "min",
  },
  it: {
    title: "Corsi",
    subtitle: "Confronta i corsi per tempo, difficolta e focus.",
    seoTitle: "Corsi AI per creator senza background tecnico",
    seoDescription:
      "Scopri corsi AI e no-code passo dopo passo per trasformare un'idea in un prodotto funzionante. Filtra per durata, difficolta e focus.",
    introPrimary: "Scegli un corso AI pratico e costruisci un risultato reale senza sovraccarico tecnico.",
    introSecondary: "Filtra per durata, difficolta e obiettivo per completare il prossimo passo con chiarezza.",
    quickLinksLabel: "Esplora dopo",
    helpLinkLabel: "Aiuto / FAQ",
    updatesLinkLabel: "Aggiornamenti prodotto",
    helper: "Presto arriveranno altri corsi.",
    filtersTitle: "Filtri",
    filters: {
      category: "Categoria",
      time: "Tempo per completare",
      difficulty: "Difficolta",
      access: "Accesso",
    },
    categories: {
      coding: "Coding",
      practice: "Pratica",
      mindset: "Mindset",
    },
    timeOptions: {
      lt30: "< 30 min",
      min30to60: "30–60 min",
      hr1to2: "1–2 ore",
    },
    difficultyOptions: {
      beginner: "Principiante",
      standard: "Standard",
      advanced: "Avanzato",
    },
    accessOptions: {
      free: "Gratis",
      paid: "A pagamento",
    },
    sortLabel: "Ordina",
    sort: {
      recommended: "Consigliato",
      shortest: "Piu brevi prima",
      beginnerFriendly: "Per principianti",
      newest: "Nuovo",
    },
    openFilters: "Filtri",
    closeFilters: "Chiudi filtri",
    resultsLabel: "{count} corsi",
    clearFilters: "Azzera filtri",
    empty: "Nessun corso corrisponde a questi filtri.",
    viewDetails: "Vedi dettagli",
    noPreview: "Nessuna anteprima",
    problemFallback: "Fa avanzare un blocco.",
    durationUnit: "min",
  },
  pt: {
    title: "Cursos",
    subtitle: "Compare cursos por tempo, dificuldade e foco.",
    seoTitle: "Cursos de IA para criadores sem perfil tecnico",
    seoDescription:
      "Explore cursos de IA e no-code passo a passo para transformar ideias em produtos funcionais. Filtre por tempo, dificuldade e foco.",
    introPrimary: "Escolha um curso pratico de IA e avance rumo a um produto real sem sobrecarga tecnica.",
    introSecondary: "Filtre por tempo, dificuldade e objetivo para dar o proximo passo com clareza.",
    quickLinksLabel: "Explorar mais",
    helpLinkLabel: "Ajuda / FAQ",
    updatesLinkLabel: "Novidades do produto",
    helper: "Mais cursos em breve.",
    filtersTitle: "Filtros",
    filters: {
      category: "Categoria",
      time: "Tempo para concluir",
      difficulty: "Dificuldade",
      access: "Acesso",
    },
    categories: {
      coding: "Codigo",
      practice: "Pratica",
      mindset: "Mentalidade",
    },
    timeOptions: {
      lt30: "< 30 min",
      min30to60: "30–60 min",
      hr1to2: "1–2 horas",
    },
    difficultyOptions: {
      beginner: "Iniciante",
      standard: "Intermediario",
      advanced: "Avancado",
    },
    accessOptions: {
      free: "Gratuito",
      paid: "Pago",
    },
    sortLabel: "Ordenar",
    sort: {
      recommended: "Recomendado",
      shortest: "Mais curto primeiro",
      beginnerFriendly: "Para iniciantes",
      newest: "Novo",
    },
    openFilters: "Filtros",
    closeFilters: "Fechar filtros",
    resultsLabel: "{count} cursos",
    clearFilters: "Limpar filtros",
    empty: "Nenhum curso corresponde a esses filtros.",
    viewDetails: "Ver detalhes",
    noPreview: "Sem visualizacao",
    problemFallback: "Avanca um bloqueio.",
    durationUnit: "min",
  },
});

export function getCoursesMarketplaceCopy(lang: Locale): CoursesMarketplaceCopy {
  return coursesMarketplaceCopyByLocale[lang];
}
